/**
 * @file        node-localExec.js       Node-specific support for creating/running localExec
 *                                      jobs with the Compute API.
 * @author      Wes Garland, wes@kingsds.network
 * @date        May 2020
 */

/* global dcpConfig */
// @ts-nocheck

const utils = require('dcp/utils');
const EventEmitter = require('events');
const DCP_ENV = require('dcp/common/dcp-env');
const { DCPError } = require('dcp/common/dcp-error');
const debugging = require('dcp/debugging').scope('dcp-client');
const isDebugging = debugging('localExec');

const debug = (...args) => {
  if (isDebugging) {
    console.debug('dcp-client:localExec', ...args);
  }
};

/**
 * Factory function which makes NodeEvaluator constructors based on the current configuration.
 */
exports.nodeEvaluatorFactory = function evaluators$$nodeEvaluatorFactory(inTesting = false)
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const path = require('path');
  var   controlCodeFiles = [];  /* Evaluator's control code establishes the sandbox environment and postMessage/onmessage implementation */

  if (DCP_ENV.platform !== 'nodejs') {
    throw new Error(`Can't create NodeEvaluator constructor: platform is '${DCP_ENV.platform}'; must be 'nodejs'.`);
  }

  /**
   * The `standaloneWorker.js` and `evaluator-node.js` currently resides in
   * `dcp-client` and that this code can be executed from source or from the
   * dcp-client bundle, we need to detect whether we're running in either mode.
   *
   * If we're running from source, `dcp-rtlink` has most likely been initialized
   * so we can resolve `dcp-client` in the `node_modules` folder of the `dcp`
   * repo.
   *
   * Else if we're running from the bundle, `dcp-rtlink` has not been
   * initialized so this code is in the bundle being evaluated in the context of
   * `dcp-client/index.js`. In that case, we need to switch to relative paths to
   * resolve the modules.
   *
   * @todo TODO(bryan-hoang): Improve detection of whether the dcp-rtlink module has
   * been initialized. e.g. Exposed an API through the dcp-rtlink module
   */
  const hasRtlinkBeenInitialized =
    Object.keys(requireNative('module')._cache)
      .some((key) => key.includes('dcp-rtlink'))
  const dcpClientModulePrefix = hasRtlinkBeenInitialized ? 'dcp-client' : '.';
  const { workerFactory } = requireNative(
    `${dcpClientModulePrefix}/lib/standaloneWorker`,
  );
  const { Evaluator } = requireNative(`${dcpClientModulePrefix}/libexec/evaluator-node`);
  
  /**
   * @param socket "server" socket for the node evaluator
   */
  function localExecConnectHandler(socket)
  {
    new Evaluator(socket, socket, controlCodeFiles);
  }

  /**
   * Constructor for a NodeEvaluator.  An instance of this class becomes the SandboxConstructor 
   * in the implementation of job::localExec() when running under NodeJS.
   *
   * @todo Eliminate the extra wrapper here.  It was previously necessary when we had to
   *       capture the factory argument vector in a closure. It should be possible now to
   *       implement this as a single function.
   */
  function NodeEvaluator()
  {
    const child_process = requireNative('child_process');
    const process = requireNative('process');
    const env = Object.assign({}, process.env, {
      /**
       * Assume the user is okay with the security implications of executing
       * their work locally. Needed to start the script without a warning
       * and delay. All other environment properties are inherited from the
       * calling process.
       */
      I_WANT_AN_INSECURE_DCP_WORKER: 'badly',
      DCP_SCHEDULER_LOCATION: `${dcpConfig.scheduler.location.href}`,
    });
    var command = false;
    var error;
    var pipe = require('dcp/utils/tmpfiles').createTempSocket(undefined, 'localExec-pipe', localExecConnectHandler);
    var options = {
      readStream: pipe,
      writeStream: pipe
    };
    
    // @TODO: make sure npm link, dcp-rtlink, and normal all work!!!
    var sandboxDefinitions;

    sandboxDefinitions = requireNative(`./generated/sandbox-definitions.json`)
    const sandboxSetupDefs = sandboxDefinitions[inTesting ? 'nodeTesting' : 'node'];
    
    for (let def of sandboxSetupDefs)
    {
      if (def.match(/\//))
        controlCodeFiles.push(requireNative('.').__require.resolve(def));
      else
        controlCodeFiles.push(requireNative.resolve(`./libexec/sandbox/` + def));
    }

    debug('Read controlCodeFiles:', controlCodeFiles);

    return new (workerFactory(options))();
  }

  NodeEvaluator.prototype = new EventEmitter('nodeLocalExec');
  return NodeEvaluator;
}
